import { usePropertyContext } from "@app/app/hooks";
import { ControlledFormField } from "@app/common/components/form/ControlledFormField";
import { Label } from "@app/common/components/form/Label";
import { Button } from "@app/componentLibrary/ui/button";
import { Input } from "@app/componentLibrary/ui/form/Input";
import { resetPasswordBookingWhizzMember } from "@app/loyalty/queries";
import { IBookingWhizzMemberResetPasswordFormFields } from "@app/loyalty/types";
import { Fragment, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface IBookingWhizzMemberResetPasswordFormProps {}

export const BookingWhizzMemberResetPasswordForm = ({}: IBookingWhizzMemberResetPasswordFormProps) => {
    const { activePropertyId } = usePropertyContext();
    const formMethods = useForm<IBookingWhizzMemberResetPasswordFormFields>({
        defaultValues: {},
        mode: "onChange",
    });
    const { t } = useTranslation(["vendorLoyalty"]);

    const [isResetSuccessfully, setIsResetSuccessfully] = useState<boolean>(false);
    const [emailValue, setEmailValue] = useState<string>("");
    const [isAbleToSubmit, setIsAbleToSubmit] = useState<boolean>(false);

    useEffect(() => {
        const isEmailValid = emailValue.trim() !== "";

        setIsAbleToSubmit(isEmailValid);
    }, [emailValue]);

    const handleSubmit = () => {
        resetPasswordBookingWhizzMember(activePropertyId, emailValue).then(() => {
            setIsResetSuccessfully(true);
        });
    };

    return (
        <Fragment>
            {isResetSuccessfully && (
                <div className="w-full p-2 rounded border-1 border-green-100 bg-green-100/10 mb-4 text-sm">
                    <h2 className="font-bold">{t("passwordReset.successMessage.title")}</h2>
                    <p>{t("passwordReset.successMessage.message")}</p>
                </div>
            )}
            {!isResetSuccessfully && (
                <FormProvider {...formMethods}>
                    <div className="text-left mb-3">
                        <Label>{t("passwordReset.email.label")}</Label>

                        <ControlledFormField
                            data-testid="email-input-field"
                            rules={{
                                required: t("passwordReset.email.errorRequired"),
                                pattern: {
                                    value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                                    message: t("passwordReset.email.errorInvalid"),
                                },
                            }}
                            onChange={(e) => setEmailValue(e.currentTarget.value)}
                            component={Input}
                            autoComplete="email"
                            placeholder={t("passwordReset.email.label")}
                            name="email"
                        />
                    </div>

                    <Button
                        data-testid="booking-whizz-login-button"
                        className="w-full mt-6"
                        disabled={!formMethods.formState.isValid || !isAbleToSubmit}
                        onClick={handleSubmit}
                    >
                        {t("passwordReset.submit")}
                    </Button>
                </FormProvider>
            )}
        </Fragment>
    );
};
